import type { ShowName } from 'aiControlledCommands/validationRejection';
import type {
  AiLabelRule,
  AvailableLabelFields,
  LabelFieldOption,
} from './command';

const toFieldNames = (fields: LabelFieldOption[]): string[] =>
  fields.map(({ name }) => name);

const toTypeKey = ({ type, typeName }: AiLabelRule): string =>
  `${type}-${typeName ?? ''}`;

const isSameType = (rule: AiLabelRule, otherRule: AiLabelRule): boolean =>
  toTypeKey(rule) === toTypeKey(otherRule);

const describeRuleTarget = (
  { type, typeName }: AiLabelRule,
  showName: ShowName
): string =>
  typeName ? `${type} type '${showName(typeName)}'` : `any ${type} type`;

const getFieldNamesInCurrentRule = (
  currentFieldRules: AiLabelRule[],
  rule: AiLabelRule
): string[] =>
  currentFieldRules
    .filter(currentRule => isSameType(currentRule, rule))
    .flatMap(({ fields }) => fields);

const hasCurrentRuleForType = (
  currentFieldRules: AiLabelRule[],
  rule: AiLabelRule
): boolean =>
  currentFieldRules.some(currentRule => isSameType(currentRule, rule));

const getCustomFieldNamesForRule = (
  fieldNamesByType: Record<string, string[]>,
  typeName: string | undefined
): string[] => {
  if (!typeName) return Object.values(fieldNamesByType).flat();
  if (!Object.hasOwn(fieldNamesByType, typeName)) return [];
  return fieldNamesByType[typeName];
};

const quoteNames = (names: string[], showName: ShowName): string =>
  names.map(name => `'${showName(name)}'`).join(', ');

export const toRejectionReasons =
  (
    availableLabelFields: AvailableLabelFields,
    currentFieldRules: AiLabelRule[],
    showName: ShowName
  ): ((rule: AiLabelRule) => string[]) =>
  rule => {
    const { builtInFields, fieldNamesByType } = availableLabelFields[rule.type];
    if (
      rule.typeName &&
      !Object.hasOwn(fieldNamesByType, rule.typeName) &&
      !hasCurrentRuleForType(currentFieldRules, rule)
    ) {
      return [`There is no ${describeRuleTarget(rule, showName)} in the view.`];
    }
    const allowedFieldNames = new Set([
      ...toFieldNames(builtInFields),
      ...getCustomFieldNamesForRule(fieldNamesByType, rule.typeName),
      ...getFieldNamesInCurrentRule(currentFieldRules, rule),
    ]);
    const unavailableFieldNames = rule.fields.filter(
      name => !allowedFieldNames.has(name)
    );
    if (!unavailableFieldNames.length) return [];
    return [
      `${quoteNames(unavailableFieldNames, showName)} cannot be shown on ${describeRuleTarget(rule, showName)}.`,
    ];
  };

export const getNewlyDuplicatedTypes = (
  rules: AiLabelRule[],
  currentFieldRules: AiLabelRule[],
  showName: ShowName
): string[] => {
  const currentRulesByType = Map.groupBy(currentFieldRules, toTypeKey);
  const rulesByType = Map.groupBy(rules, toTypeKey);
  return [...rulesByType]
    .filter(([typeKey, typeRules]) => {
      const isDuplicated = typeRules.length > 1;
      const wasDuplicated = (currentRulesByType.get(typeKey)?.length ?? 0) > 1;
      return isDuplicated && !wasDuplicated;
    })
    .map(([, [firstRule]]) => describeRuleTarget(firstRule, showName));
};

const getFieldNamesInRuleForAllTypes = (
  rules: AiLabelRule[],
  type: AiLabelRule['type']
): Set<string> =>
  new Set(
    rules
      .filter(rule => rule.type === type && !rule.typeName)
      .flatMap(({ fields }) => fields)
  );

export const getFieldsRepeatingRuleForAllTypes = (
  rules: AiLabelRule[],
  showName: ShowName
): string[] =>
  rules
    .filter(({ typeName }) => Boolean(typeName))
    .flatMap(rule => {
      const fieldNamesForAllTypes = getFieldNamesInRuleForAllTypes(
        rules,
        rule.type
      );
      const repeatedFieldNames = rule.fields.filter(name =>
        fieldNamesForAllTypes.has(name)
      );
      if (!repeatedFieldNames.length) return [];
      return [
        `${quoteNames(repeatedFieldNames, showName)} on ${describeRuleTarget(rule, showName)}`,
      ];
    });
